"use no memo";

import type { ExpandedState, Row } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import { Fragment, useCallback, useRef } from "react";

import type { features } from "~/components/common/tickets/ticket-list";
import { TicketListItem } from "~/components/common/tickets/ticket-list-item";
import type { Ticket } from "~/stores/tickets-store";

const TICKET_ITEM_HEIGHT = 44;
const TICKET_GROUP_ITEM_HEIGHT = 40;

export function TicketGroupList({
  rows,
  setExpanded,
}: {
  rows: Row<typeof features, Ticket>[];
  setExpanded: (expandedState: ExpandedState) => void;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const getScrollElement = useCallback(() => parentRef.current, []);

  const virtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: (index) =>
      (rows[index]?.subRows.length ?? 0) * TICKET_ITEM_HEIGHT +
      TICKET_GROUP_ITEM_HEIGHT,
    getScrollElement: getScrollElement,
    overscan: 1,
  });

  const virtualItems = virtualizer.getVirtualItems();

  const [paddingTop, paddingBottom] =
    virtualItems.length > 0
      ? [
          Math.max(
            0,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            virtualItems[0]!.start - virtualizer.options.scrollMargin,
          ),
          Math.max(
            0,
            virtualizer.getTotalSize() -
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              virtualItems[virtualItems.length - 1]!.end,
          ),
        ]
      : [0, 0];

  return (
    <div
      ref={parentRef}
      className="col-[1/_-1] grid min-h-0 grid-cols-subgrid overflow-x-hidden overflow-y-auto"
    >
      <div
        className="col-[1/_-1] grid min-h-0 grid-cols-subgrid content-start"
        style={{
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {paddingTop > 0 ? (
          <div data-slot="pad-top" style={{ height: paddingTop }}></div>
        ) : null}
        {virtualItems.map((virtualItem) => {
          const groupedRow = rows[virtualItem.index];
          if (!groupedRow) return null;
          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              data-group-key={`group_GROUP_${groupedRow.id}`}
              ref={virtualizer.measureElement}
              className="col-[1/_-1] grid size-full grid-cols-subgrid"
            >
              <div
                data-list-key={`GROUP_${groupedRow.id}`}
                className="bg-muted sticky top-0 z-10 col-[1/_-1] flex h-10 items-center gap-2 px-4 pl-2.5"
              >
                {groupedRow.getAllCells().map((cell) =>
                  cell.getIsGrouped() ? (
                    <Fragment key={cell.id}>
                      {groupedRow.getCanExpand() ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            if (e.shiftKey) {
                              if (groupedRow.getIsExpanded()) {
                                setExpanded({});
                              } else {
                                setExpanded(true);
                              }
                            } else {
                              groupedRow.toggleExpanded();
                            }
                          }}
                          className="cursor-pointer"
                        >
                          {groupedRow.getIsExpanded() ? (
                            <ChevronDownIcon className="size-4" />
                          ) : (
                            <ChevronRightIcon className="size-4" />
                          )}
                        </button>
                      ) : null}
                      <span className="text-sm font-medium">
                        {flexRender(
                          cell.column.columnDef.aggregatedCell,
                          cell.getContext(),
                        )}
                      </span>
                      <span className="text-muted-foreground text-sm">
                        {groupedRow.subRows.length}
                      </span>
                    </Fragment>
                  ) : null,
                )}
              </div>
              {groupedRow.getIsExpanded() ? (
                <TicketGroupSubList
                  getScrollElement={getScrollElement}
                  initialOffset={virtualizer.scrollOffset ?? 0}
                  scrollMargin={virtualItem.start + TICKET_GROUP_ITEM_HEIGHT}
                  rows={groupedRow.subRows}
                />
              ) : null}
            </div>
          );
        })}
        {paddingBottom > 0 ? (
          <div data-slot="pad-bottom" style={{ height: paddingBottom }}></div>
        ) : null}
        <div className="col-[1/_-1]"></div>
      </div>
    </div>
  );
}

export function TicketGroupSubList({
  getScrollElement,
  initialOffset,
  rows,
  scrollMargin,
}: {
  getScrollElement: () => HTMLDivElement | null;
  initialOffset: number;
  rows: Row<typeof features, Ticket>[];
  scrollMargin: number;
}) {
  const virtualizer = useVirtualizer({
    count: rows.length,
    scrollMargin,
    getScrollElement,
    estimateSize: () => TICKET_ITEM_HEIGHT,
    overscan: 25,
    initialOffset,
  });

  const virtualItems = virtualizer.getVirtualItems();

  const [paddingTop, paddingBottom] =
    virtualItems.length > 0
      ? [
          Math.max(
            0,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            virtualItems[0]!.start - virtualizer.options.scrollMargin,
          ),
          Math.max(
            0,
            virtualizer.getTotalSize() -
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              virtualItems[virtualItems.length - 1]!.end,
          ),
        ]
      : [0, 0];

  return (
    <div
      className="col-[1/_-1] grid min-h-0 w-full grid-cols-subgrid content-start"
      style={{
        height: virtualizer.getTotalSize(),
      }}
    >
      {paddingTop > 0 ? <div style={{ height: paddingTop }}></div> : null}
      {virtualItems.map((virtualItem) => {
        const row = rows[virtualItem.index];
        if (!row) return null;
        return (
          <TicketListItem
            row={row}
            key={virtualItem.key}
            data-index={virtualItem.index}
            data-row-id={row.id}
            data-list-key={`ITEM_${row.original.id}`}
            ref={virtualizer.measureElement}
          />
        );
      })}
      {paddingBottom > 0 ? <div style={{ height: paddingBottom }}></div> : null}
    </div>
  );
}
